import { Injectable } from '@angular/core'

import { Observable } from 'rxjs'

import {
  CopyPasteEntitiesResponse,
  DataModelAttribute,
  DataModelAttributeValidator,
  DataModelTypeEntity,
  ProjectDataModelResponse,
  newDataTypePayload,
} from '@flowx/designer/modules/core/dictionary/data-model.dictionary'

import { ApiHttpProxyService, ForwardProxyMicroservice } from './api-http-proxy.service'

import {
  AppResource,
  AppResourceWithClone,
  ApplicationDependencyType,
  ApplicationResourceType,
  ManifestInfo,
  ProxyApiAction,
} from '../entities/application.entity'
import {
  BulkUpdateAttributeParams,
  CreateDataModelAttributeParams,
  UpdateDataModelAttributeParams,
} from '../entities/data-model.entity'

@Injectable({ providedIn: 'root' })
export class ProjectDataModelApiService extends ApiHttpProxyService {
  private url = 'api/project-data-type'

  getProjectDataModel(): Observable<
    AppResource<ProjectDataModelResponse> & {
      manifestInfo: ManifestInfo[]
    }
  > {
    return this.get(
      `${this.url}/all`,
      {
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.ProjectDataType,
        includeDependentResources: true,
      },
      {
        params: this.addParams({
          includeManifestInfo: true,
          depTypes: ApplicationDependencyType.LIB,
        }),
      }
    )
  }

  createDataType(dataType: newDataTypePayload): Observable<AppResource<DataModelTypeEntity>> {
    return this.post(
      this.url,
      { action: ProxyApiAction.CREATE, resourceName: dataType.name, payload: dataType },
      {
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.ProjectDataType,
      }
    )
  }

  updateDataType(
    dataType: Partial<DataModelTypeEntity>
  ): Observable<AppResourceWithClone<DataModelTypeEntity>> {
    return this.patch(`${this.url}`, dataType, {
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.ProjectDataType,
      resourceId: dataType.resourceId,
      action: ProxyApiAction.UPDATE,
      resourceName: dataType.name,
    })
  }

  deleteDataType(
    resourceId: DataModelTypeEntity['resourceId'],
    force = true
  ): Observable<AppResource<void>> {
    return this.delete(
      `${this.url}`,
      { params: { force } },
      {
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.ProjectDataType,
        resourceId,
      }
    )
  }

  createAttribute(
    dataType: Pick<DataModelTypeEntity, 'resourceId'>,
    attribute: CreateDataModelAttributeParams
  ): Observable<AppResourceWithClone<DataModelAttribute>> {
    return this.patch(`${this.url}/attributes`, attribute, {
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.ProjectDataType,
      action: ProxyApiAction.CREATE,
      resourceId: dataType.resourceId,
    })
  }

  deleteAttributeInProjectDataModelType(
    resourceId: DataModelTypeEntity['resourceId'],
    attributeFlowxUuid: DataModelAttribute['flowxUuid']
  ): Observable<AppResourceWithClone<DataModelAttribute>> {
    return this.patch(
      `${this.url}/attributes/${attributeFlowxUuid}`,
      {},
      {
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.ProjectDataType,
        action: ProxyApiAction.DELETE,
        resourceId,
      }
    )
  }

  updateAttributeInProjectDataModelType(
    resourceId: DataModelTypeEntity['resourceId'],
    attribute: UpdateDataModelAttributeParams
  ): Observable<AppResourceWithClone<DataModelAttribute>> {
    return this.patch(`${this.url}/attributes/${attribute.flowxUuid}`, attribute, {
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.ProjectDataType,
      resourceId,
      action: ProxyApiAction.UPDATE,
    })
  }

  updateAttributeValidatorsInProjectDataModelType(
    resourceId: DataModelTypeEntity['resourceId'],
    attributeFlowxUuid: DataModelAttribute['flowxUuid'],
    validators: DataModelAttributeValidator[]
  ): Observable<AppResourceWithClone<DataModelAttribute>> {
    return this.patch(
      `${this.url}/attributes/${attributeFlowxUuid}/validators`,
      { validators },
      {
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.ProjectDataType,
        resourceId,
        action: ProxyApiAction.UPDATE,
      }
    )
  }

  bulkUpdateAttributes(
    resourceId: DataModelTypeEntity['resourceId'],
    attributeUpdates: BulkUpdateAttributeParams
  ): Observable<AppResourceWithClone<DataModelAttribute[]>> {
    return this.patch(`${this.url}/attributes/bulk`, attributeUpdates, {
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.ProjectDataType,
      resourceId,
      action: ProxyApiAction.PARTIAL_UPDATE,
    })
  }
  pasteEntities(
    copyPasteStructure: CopyPasteEntitiesResponse,
    changeNamesIfExists: boolean = false
  ): Observable<AppResource<DataModelTypeEntity[]>> {
    const resourcesNames = copyPasteStructure.dataEntities.map((entity) => entity.name)

    return this.post(
      `${this.url}/paste`,
      { payload: copyPasteStructure, resourcesNames, action: ProxyApiAction.CREATE },
      {
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.ProjectDataType,
        action: ProxyApiAction.CREATE,
        bulk: true,
        params: this.addParams({ changeNamesIfExists }),
      }
    ) as Observable<AppResource<DataModelTypeEntity[]>>
  }
}
